// Site content (text, colours, collection pages, email templates) kept in
// the Supabase site_content table: one row per key, value is JSON.
// Anyone may read; only the admin may save (database rule).
//
//   loadContent()          -> { content: { key: value, ... }, error }
//   saveContent(updates)   -> { error }      updates = { key: value, ... }
//
// Errors are plain English, safe to show in a toast.

import { supabase } from './supabaseClient';

var LOAD_TIMEOUT_MS = 8000;
var KEY_PATTERN = /^[a-z0-9_]{1,64}$/;

export var NOT_CONNECTED = 'Not connected to the database. Changes were not saved.';
export var LOAD_FAILED = "Couldn't load the site text from the database.";
export var SAVE_FAILED = 'Could not save. Please check your internet connection and try again.';
export var NOT_ALLOWED = 'You need to be signed in as the admin to save. Please sign in again and retry.';

function withTimeout(promise, ms) {
  var timer;
  var timeout = new Promise(function(resolve) {
    timer = setTimeout(function() { resolve({ data: null, error: { message: 'timed out' } }); }, ms);
  });
  return Promise.race([promise, timeout]).finally(function() { clearTimeout(timer); });
}

export async function loadContent() {
  if (!supabase) return { content: {}, error: LOAD_FAILED };
  try {
    var result = await withTimeout(
      Promise.resolve(supabase.from('site_content').select('key, value')),
      LOAD_TIMEOUT_MS
    );
    if (result.error || !Array.isArray(result.data)) {
      console.error('Could not load site content:', result.error ? result.error.message : 'no data');
      return { content: {}, error: LOAD_FAILED };
    }
    var content = {};
    result.data.forEach(function(row) {
      if (row && typeof row.key === 'string' && row.value !== null && row.value !== undefined) {
        content[row.key] = row.value;
      }
    });
    return { content: content, error: null };
  } catch (e) {
    console.error('Could not load site content:', e);
    return { content: {}, error: LOAD_FAILED };
  }
}

export async function saveContent(updates) {
  if (!updates || typeof updates !== 'object' || Array.isArray(updates)) {
    return { error: 'Nothing to save.' };
  }
  var keys = Object.keys(updates);
  if (keys.length === 0) return { error: null };

  var badKey = keys.find(function(k) { return !KEY_PATTERN.test(k); });
  if (badKey) return { error: 'Could not save: "' + badKey + '" is not a valid setting name.' };
  var empty = keys.find(function(k) { return updates[k] === undefined || updates[k] === null; });
  if (empty) return { error: 'Could not save: "' + empty + '" has no value.' };

  if (!supabase) return { error: NOT_CONNECTED };

  var rows = keys.map(function(k) { return { key: k, value: updates[k] }; });
  try {
    var result = await supabase
      .from('site_content')
      .upsert(rows, { onConflict: 'key' })
      .select('key');
    if (result.error) {
      console.error('Site content save failed:', result.error);
      var code = result.error.code;
      if (code === '42501' || result.status === 401 || result.status === 403) return { error: NOT_ALLOWED };
      return { error: SAVE_FAILED };
    }
    if (!result.data || result.data.length !== rows.length) {
      console.error('Site content save: expected', rows.length, 'rows, got', result.data);
      return { error: NOT_ALLOWED };
    }
    return { error: null };
  } catch (e) {
    console.error('Site content save failed:', e);
    return { error: SAVE_FAILED };
  }
}
